import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { logger } from '@tunasong/models'
import type { Entity, Persisted } from '@tunasong/schemas'

export interface EntitiesState {
  eTags: { [id: string]: string | null }
  /**
   * The server's entity as of `eTags[id]`: the version a conditional save is based on. On a 412 it is what tells
   * "somebody changed the fields I am saving" from "somebody changed other fields" (GH #1912).
   */
  eTagBases: { [id: string]: Persisted<Entity> }
  /**
   * Structured fields held as unsaved because someone else changed them too (GH #1912 option B): my latest edit and
   * the server's version, per entity, until the conflict notice resolves them. See `conditional-save.ts`.
   */
  conflicts: { [id: string]: EntityConflict }
  /**
   * Bumped when "Use their version" or its Undo replaced what an editor showed (GH #1912). Editors that hold their
   * value in uncontrolled inputs or a local draft key on it, so they show the store's value rather than what was typed.
   */
  conflictResolutions: { [id: string]: number }
  receivedEntities: Persisted<Entity>[]
}

export interface EntityConflict {
  /** The held fields, in the order they were first held */
  fields: string[]
  /** My latest edit to the held fields */
  mine: Partial<Entity>
  /** The server's version of the held fields when the conflict was found */
  theirs: Partial<Entity>
}

// Define the initial state using that type
export const initialState: EntitiesState = {
  eTags: {},
  eTagBases: {},
  conflicts: {},
  conflictResolutions: {},
  receivedEntities: [],
}

export const entitiesSlice = createSlice({
  name: 'entities',
  initialState,
  reducers: {
    /** We use eTags to avoid overwriting metadata on Entities. Most editing is through an YDoc, which does not need any sync */
    setEtag: (state, action: PayloadAction<{ id: string; eTag: string; base?: Persisted<Entity> }>) => {
      const { id, eTag, base } = action.payload
      state.eTags[id] = eTag
      if (base) {
        state.eTagBases[id] = base
      }
    },
    /** Hold fields as conflicting, or add to a conflict already held: my edit and theirs merge in, field by field */
    holdConflict: (state, action: PayloadAction<{ id: string } & EntityConflict>) => {
      const { id, fields, mine, theirs } = action.payload
      const held = state.conflicts[id]
      state.conflicts[id] = held
        ? {
            fields: [...held.fields, ...fields.filter(field => !held.fields.includes(field))],
            mine: { ...held.mine, ...mine },
            theirs: { ...held.theirs, ...theirs },
          }
        : { fields, mine, theirs }
    },
    /** A later edit to fields already held: it replaces my held edit, and is not sent */
    updateHeldEdit: (state, action: PayloadAction<{ id: string; mine: Partial<Entity> }>) => {
      const { id, mine } = action.payload
      const held = state.conflicts[id]
      if (held) {
        held.mine = { ...held.mine, ...mine }
      }
    },
    /** "Use their version" or its Undo replaced what the editors show */
    bumpConflictResolution: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload
      state.conflictResolutions[id] = (state.conflictResolutions[id] ?? 0) + 1
    },
    clearConflict: (state, action: PayloadAction<{ id: string }>) => {
      delete state.conflicts[action.payload.id]
    },
    /** Resolve some held fields only, as one panel's banner does; the conflict clears with its last field */
    releaseHeldFields: (state, action: PayloadAction<{ id: string; fields: readonly string[] }>) => {
      const { id, fields } = action.payload
      const held = state.conflicts[id]
      if (!held) {
        return
      }
      const remaining = held.fields.filter(field => !fields.includes(field))
      if (remaining.length === 0) {
        delete state.conflicts[id]
        return
      }
      const keep = <T extends object>(source: T) =>
        Object.fromEntries(Object.entries(source).filter(([key]) => remaining.includes(key))) as T
      state.conflicts[id] = { fields: remaining, mine: keep(held.mine), theirs: keep(held.theirs) }
    },
    /** Receive the entity from the backend */
    receiveEntity: (state, action: PayloadAction<{ entity: Persisted<Entity>; eTag?: string | null }>) => {
      /** @no-op - handle this action in entitiesApi */
      logger.debug('Received entity', action.payload.entity)
      /**
       * GH #1666 / #1640: this was append-only, so every backend push — including
       * re-receiving the SAME entity on each Performance-mode song switch —
       * appended a fresh full `Persisted<Entity>` (with its document subtree) and
       * never released the old copy. Page-turning a fixed set grew the heap
       * ~0.9MB/round monotonically. The sole consumer (`mergeChatComments`) keys
       * by id and keeps the newest, so collapse duplicates here: drop any prior
       * copy of this id, then append the fresh one. Bounds the array to the
       * distinct-entity count rather than the number of pushes.
       */
      const { entity } = action.payload
      state.receivedEntities = [...state.receivedEntities.filter(e => e.id !== entity.id), entity]
    },
    receivePartialEntity: (state, action: PayloadAction<Partial<Persisted<Entity>> & { id: string }>) => {
      /**
       * @no-op - we handle this in entities-middleware
       */
      logger.debug('Received partial entity', action.payload)
    },
  },
})
